import { HttpContext, HttpContextToken } from '@angular/common/http';

export const SKIP_AUTH_HEADER_CONTEXT = new HttpContextToken<boolean>(() => false);
export const SKIP_REFRESH_JWT_TOKEN_INTERCEPTOR_HEADER_KEY = 'X-Skip-Refresh-Jwt-Token-Interceptor';
export const FORCE_REFRESH_JWT_TOKEN_CONTEXT = new HttpContextToken<boolean>(() => false);

/**
 * Credits: https://github.com/auth0/angular2-jwt/blob/main/projects/angular-jwt/src/lib/jwthelper.service.ts
 */
const _urlBase64Decode = (str: string): string => {
  let output = str.replace(/-/g, '+').replace(/_/g, '/');
  switch (output.length % 4) {
    case 0: {
      break;
    }
    case 2: {
      output += '==';
      break;
    }
    case 3: {
      output += '=';
      break;
    }
    default: {
      throw new Error('Illegal base64url string!');
    }
  }
  return _b64DecodeUnicode(output);
};

// credits for decoder goes to https://github.com/atk
const _b64decode = (str: string): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
  let output = '';

  str = String(str).replace(/=+$/, '');

  if (str.length % 4 === 1) {
    throw new Error(`'atob' failed: The string to be decoded is not correctly encoded.`);
  }

  for (
    // initialize result and counters
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let bc = 0, bs = 0, buffer: any, idx = 0;
    // get next character
    (buffer = str.charAt(idx++));
    // character found in table? initialize bit storage and add its ascii value;
    ~buffer &&
    ((bs = bc % 4 ? bs * 64 + buffer : buffer),
    // and if not first of each 4 characters,
    // convert the first 8 bits to one ascii character
    bc++ % 4)
      ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
      : 0
  ) {
    // try to find character in table (0-63, not found => -1)
    buffer = chars.indexOf(buffer);
  }
  return output;
};

const _b64DecodeUnicode = (str: string) => {
  return decodeURIComponent(
    Array.prototype.map
      .call(_b64decode(str), (c: string) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _decodeToken = <T = any & { exp: number }>(token: string): T | null => {
  if (!token || token === '') {
    return null;
  }

  const parts = token.split('.');

  if (parts.length !== 3) {
    throw new Error(
      `The inspected token doesn't appear to be a JWT. Check to make sure it has three parts and see https://jwt.io for more.`
    );
  }

  const decoded = _urlBase64Decode(parts[1]);
  if (!decoded) {
    throw new Error('Cannot decode the token.');
  }

  return JSON.parse(decoded);
};

export const getTokenExpirationDate = (token: string): Date | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const decoded: any = _decodeToken(token);

  // eslint-disable-next-line no-prototype-builtins
  if (!decoded || !decoded.hasOwnProperty('exp')) {
    return null;
  }

  const date = new Date(0);
  date.setUTCSeconds(decoded.exp);

  return date;
};

const _isTokenExpired = (token: string | null): boolean => {
  if (!token || token === '') return true;

  const date = getTokenExpirationDate(token);

  if (date === null) return false;
  return !(date.valueOf() > new Date().valueOf());
};

export const decodeAuthToken = (token: string) => {
  return _decodeToken(token);
};

export const isTokenExpired = (token: string) => {
  return _isTokenExpired(token);
};

/**
 * Vrací observe, reportProgress a options s nastaveným contextem.
 * Context bude obsahovat SKIP_AUTH_HEADER_CONTEXT a další volitelně přidané tokeny.
 *
 * @param extraContext Optional context, který bude sloučen s výchozím.
 */
export function publicApiArgs(extraContext?: HttpContext): ['body', false, { context: HttpContext }] {
  let context = extraContext ?? new HttpContext();
  context = context.set(SKIP_AUTH_HEADER_CONTEXT, true);

  return [
    'body',
    false,
    {
      context,
    },
  ];
}
